import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  LoadingButton,
  NewNumberInputField,
  NewTextInputField,
  NewTextareaField,
} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {Error, FullForm} from 'src/components/primitives';
import {Banner} from 'src/containers/Banner';
import {joiResolver} from 'src/utils/validator';

// eslint-disable-next-line no-restricted-imports
import {
  useCancelStornoProcessMutation,
  useGetStornoDataMutation,
  useGetTransactionDataMutation,
  useStartStornoProcessMutation,
  useUpdateTransactionAmountMutation,
} from '../../api';
import {
  EBilledEventState,
  EPaymentCorrectionType,
  TBilledEventData,
  TStornoFormData,
} from '../definitions';
import * as S from '../style';
import {BilledEventData} from './BilledEventData';
import {BilledEventDetails} from './BilledEventDetails';
import {showConfirmModal, showSuccessModal} from './modals';

const UNSUPPORTED_CORRECTION_ERROR = 'correction.update-operation.unsupported-original-event-type';

const formResolver = joiResolver((joi) =>
  joi.object({
    originalEventId: joi.string().trim().required(),
    comment: joi.when('$correctionType', {
      is: EPaymentCorrectionType.UPDATE_SUM,
      then: joi.when('$billedEvent', {
        is: joi.exist(),
        then: joi.string().required(),
      }),
    }),
    sum: joi.when('$correctionType', {
      is: EPaymentCorrectionType.UPDATE_SUM,
      then: joi.when('$billedEvent', {
        is: joi.exist(),
        then: joi
          .number()
          .custom((value, helpers) => {
            const {transaction, debtTransaction} = helpers.prefs.context as TBilledEventData;

            if (
              (transaction?.amount && value === Math.abs(transaction.amount)) ||
              (debtTransaction?.amount && value === Math.abs(debtTransaction.amount))
            ) {
              return helpers.error('^Corrections:payments.storno.duplicateSumError');
            }

            return value;
          })
          .required(),
      }),
    }),
  }),
);

const useSearchEventId = (correctionType: EPaymentCorrectionType) => {
  const stornoHookData = useGetStornoDataMutation();
  const transactionHookData = useGetTransactionDataMutation();

  const data = React.useMemo(() => {
    const hookData =
      correctionType === EPaymentCorrectionType.UPDATE_SUM ? transactionHookData : stornoHookData;

    return [
      hookData[0],
      {
        ...hookData[1],
        reset: () => {
          transactionHookData[1].reset();
          stornoHookData[1].reset();
        },
      },
    ];
  }, [correctionType, stornoHookData, transactionHookData]);

  return data as unknown as typeof stornoHookData;
};

export const StornoActions = React.memo<{correctionType: EPaymentCorrectionType}>(
  ({correctionType}) => {
    const {t} = useTranslation();
    const [billedEventData, setBilledEventData] = React.useState<TBilledEventData | undefined>();

    const methods = useForm<TStornoFormData>({
      defaultValues: {
        originalEventId: null,
        comment: '',
        sum: undefined,
      },
      resolver: formResolver,
      context: {
        context: {...billedEventData, correctionType},
      },
    });

    const [startStornoProcess] = useStartStornoProcessMutation();
    const [cancelStornoProcess] = useCancelStornoProcessMutation();
    const [updateTransactionSumProcess] = useUpdateTransactionAmountMutation();
    const {setValue} = methods;
    const {originalEventId, comment, sum} = methods.watch();

    const [searchData, {reset: resetSearch, data: eventData, isLoading, error: originalError}] =
      useSearchEventId(correctionType);
    const error = originalError as
      | undefined
      | {status: number; data: {code: string; args: {correctionInfo: TBilledEventData}}};

    React.useEffect(() => {
      setBilledEventData(eventData);
    }, [eventData]);

    const handleSearchStorno = React.useCallback(
      (values: TStornoFormData) => {
        searchData(values.originalEventId!);
      },
      [searchData],
    );

    const isAlreadyStorned =
      correctionType === EPaymentCorrectionType.STORNO &&
      [EBilledEventState.STORNO, EBilledEventState.STORNED].includes(
        billedEventData?.transaction?.state as EBilledEventState,
      );

    const resetBilledEventData = React.useCallback(() => {
      resetSearch();
      setValue('originalEventId', '');
      setValue('comment', '');
      setValue('sum', null);
    }, [setValue, resetSearch]);

    const handleStartStornoProcess = React.useCallback(async () => {
      try {
        await startStornoProcess({originalEventId, comment}).unwrap();

        showSuccessModal({onClose: resetBilledEventData});
      } catch {}
    }, [comment, originalEventId, resetBilledEventData]);

    const handleCancelStornoProcess = React.useCallback(async () => {
      if (!originalEventId) {
        return;
      }

      try {
        await cancelStornoProcess(originalEventId).unwrap();

        showSuccessModal({onClose: resetBilledEventData});
      } catch {}
    }, [originalEventId, resetBilledEventData]);

    const handleUpdateSumProcess = React.useCallback(async () => {
      if (!originalEventId || !sum) {
        return;
      }

      try {
        await updateTransactionSumProcess({
          originalEventId,
          newAmount: -sum,
          comment,
        }).unwrap();

        showSuccessModal({onClose: resetBilledEventData});
      } catch {}
    }, [originalEventId, resetBilledEventData, sum, comment]);

    const submitFunctions = React.useMemo(
      () =>
        ({
          [EPaymentCorrectionType.STORNO]: handleStartStornoProcess,
          [EPaymentCorrectionType.CANCEL_STORNO]: handleCancelStornoProcess,
          [EPaymentCorrectionType.UPDATE_SUM]: handleUpdateSumProcess,
        }) as Record<EPaymentCorrectionType, TAnyFunction>,
      [handleCancelStornoProcess, handleStartStornoProcess, handleUpdateSumProcess],
    );

    const handleCorrection = React.useCallback(() => {
      if (!correctionType) {
        return;
      }

      return showConfirmModal({
        correctionType,
        billedEventData,
        onSubmit: submitFunctions[correctionType],
        onCancel: resetBilledEventData,
      });
    }, [correctionType, resetBilledEventData, billedEventData, submitFunctions]);

    return (
      <FormProvider {...methods}>
        <FullForm onSubmit={methods.handleSubmit(handleSearchStorno)}>
          <Stack size="4" isFullWidth>
            <Stack size="5" direction="ROW" isFullWidth align="start">
              <NewTextInputField
                name="originalEventId"
                label={t('Corrections:payments.storno.enterEventId')}
                noMargin
              />
              <S.SearchButton appearance="primary" loading={isLoading} type="submit">
                {t(`Corrections:payments.storno.search`)}
              </S.SearchButton>
            </Stack>
            {error?.status === 404 && (
              <Stack size="2">
                <S.BigText secondary>{t('Corrections:payments.searchResults')}</S.BigText>
                <Error>
                  <S.StatusIcon name="error" />
                  {t(`Corrections:payments.notFound`)}
                </Error>
              </Stack>
            )}
            {error?.data.code === UNSUPPORTED_CORRECTION_ERROR && (
              <div>
                <S.BigText secondary>{t('Corrections:payments.searchResults')}</S.BigText>
                <BilledEventDetails billedEventData={error.data.args.correctionInfo} />
                <Error>
                  <S.StatusIcon name="error" />
                  {t(`Corrections:payments.storno.invalidEventType`)}
                </Error>
              </div>
            )}
            {billedEventData && (
              <>
                <Stack isFullWidth>
                  <S.BigText secondary>{t('Corrections:payments.searchResults')}</S.BigText>
                  <BilledEventDetails billedEventData={billedEventData} />
                  {correctionType === EPaymentCorrectionType.UPDATE_SUM && (
                    <NewNumberInputField
                      name="sum"
                      label={t('Corrections:payments.storno.enterSum')}
                      isDecimal
                    />
                  )}
                  <Banner type="WARNING" fontSize={'S'}>
                    {t('Corrections:payments.storno.isSearchCorrectMessage')}
                  </Banner>
                  <BilledEventData data={billedEventData} />
                </Stack>
                {[EPaymentCorrectionType.STORNO, EPaymentCorrectionType.UPDATE_SUM].includes(
                  correctionType,
                ) && (
                  <NewTextareaField
                    name="comment"
                    label={t('Corrections:payments.storno.comment')}
                    noMargin
                  />
                )}
                <LoadingButton
                  appearance="primary"
                  onClick={methods.handleSubmit(handleCorrection)}
                  disabled={isAlreadyStorned}
                  size="L"
                >
                  {t(`Corrections:payments.storno.button.${correctionType}`)}
                </LoadingButton>
              </>
            )}
          </Stack>
        </FullForm>
      </FormProvider>
    );
  },
);

StornoActions.displayName = 'StornoActions';
